import React, { useState } from 'react';
import { Collapse } from '@material-ui/core';
import { ExpandMoreOutlined } from '@material-ui/icons';

const PERSONAL_FIELDS = new Set([
  'educationLevel', 'gender', 'birthDate', 'nationality', 'region', 'comuna',
  'socialPercentage', 'participatedMentoring', 'completedCourses',
  'email', 'avatar', 'instagram', 'linkedin',
]);
const COMPANY_FIELDS = new Set([
  'businessName', 'isFormalized', 'formalizationType', 'formalizationYear',
  'formalizationReason', 'yearsInBusiness', 'productServiceType',
  'productServiceCategory', 'companySize', 'numberOfEmployees',
  'facebookUrl', 'instagramUrl', 'linkedinUrl',
]);
const MENTOR_FIELDS = new Set([
  'profession', 'mentorExperience', 'mentoringTopics', 'helping', 'pillars',
]);

const groupMissingFields = (missingFields, roleType) => {
  const groups = [
    {
      key: 'personal', title: 'Datos personales',
      singular: 'dato personal', plural: 'datos personales', fields: [],
    },
    {
      key: 'profile',
      title: roleType === 'mentor'
        ? 'Datos de experiencia laboral'
        : 'Datos de empresa',
      singular: roleType === 'mentor'
        ? 'dato de experiencia laboral'
        : 'dato de empresa',
      plural: roleType === 'mentor'
        ? 'datos de experiencia laboral'
        : 'datos de empresa',
      fields: [],
    },
    {
      key: 'other', title: 'Otros pasos del registro',
      singular: 'paso del registro', plural: 'pasos del registro', fields: [],
    },
  ];

  // Group the API results as they appear in the registration form, preserving
  // labels, validation reasons and any future fields that are not mapped yet.
  missingFields.forEach((field) => {
    let group = groups[2];
    if (field.key === 'discoverySource') {
      group = groups[roleType === 'mentor' ? 1 : 0];
    } else if (PERSONAL_FIELDS.has(field.key)) {
      group = groups[0];
    } else if (
      (roleType === 'mentor' && MENTOR_FIELDS.has(field.key)) ||
      (roleType === 'mentee' && COMPANY_FIELDS.has(field.key))
    ) {
      group = groups[1];
    }
    group.fields.push(field);
  });

  return groups.filter((group) => group.fields.length > 0);
};

const RegistrationMissingFields = ({
  id, expanded, onChange, missingFields, roleType,
}) => {
  const hasDetails = Array.isArray(missingFields);
  const groups = hasDetails ? groupMissingFields(missingFields, roleType) : [];
  const [selectedGroupKey, setSelectedGroupKey] = useState(null);
  const selectedGroup =
    groups.find((group) => group.key === selectedGroupKey) || groups[0];
  const selectedToggleId = selectedGroup
    ? `${id}-${selectedGroup.key}-toggle`
    : `${id}-detail-toggle`;

  const toggleGroup = (event, group) => {
    const isOpen = expanded && selectedGroup?.key === group.key;
    setSelectedGroupKey(group.key);
    onChange(event, !isOpen);
  };

  return (
    <div className="session-overview__missing-fields">
      <div className="session-overview__missing-summary">
        <span className="session-overview__missing-title">Campos faltantes</span>
        <div
          className="session-overview__missing-controls"
          role="group"
          aria-label="Campos faltantes por categoría"
        >
          {groups.length > 0 ? groups.map((group, index) => (
            <React.Fragment key={group.key}>
              {index > 0 && (
                <span className="session-overview__missing-separator" aria-hidden="true">
                  |
                </span>
              )}
              <button
                type="button"
                className="session-overview__missing-toggle"
                id={`${id}-${group.key}-toggle`}
                aria-controls={id}
                aria-expanded={expanded && selectedGroup?.key === group.key}
                onClick={(event) => toggleGroup(event, group)}
              >
                <span>
                  {group.fields.length}{' '}
                  {group.fields.length === 1 ? group.singular : group.plural}
                </span>
                <ExpandMoreOutlined aria-hidden="true" />
              </button>
            </React.Fragment>
          )) : (
            <button
              type="button"
              className="session-overview__missing-toggle"
              id={selectedToggleId}
              aria-controls={id}
              aria-expanded={expanded}
              onClick={(event) => onChange(event, !expanded)}
            >
              Ver detalle
              <ExpandMoreOutlined aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div
          className="session-overview__missing-details"
          id={id}
          role="region"
          aria-labelledby={selectedToggleId}
        >
          {!hasDetails ? (
            <p className="session-overview__missing-message" role="status">
              No fue posible obtener los campos faltantes. Vuelve a cargar el perfil.
            </p>
          ) : missingFields.length === 0 ? (
            <p className="session-overview__missing-message">
              No se detectaron campos faltantes. El usuario debe finalizar el
              formulario para completar su registro.
            </p>
          ) : (
            <>
              <p className="session-overview__missing-message">
                Datos y pasos que el usuario debe completar o revisar en su
                formulario de registro:
              </p>
              <div className="session-overview__missing-group">
                <h4 className="session-overview__missing-group-title">
                  {selectedGroup.title}
                </h4>
                <ul
                  className="session-overview__missing-list"
                  aria-label={selectedGroup.title}
                >
                  {selectedGroup.fields.map((field) => (
                    <li key={field.key}>
                      <span>{field.label}</span>
                      {field.reason === 'invalid' && (
                        <span className="session-overview__missing-review">
                          Revisar formato
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default RegistrationMissingFields;
